import React, { useMemo, useState } from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Drawer,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';

const NAV_ITEMS = [
  { label: 'Analysis', to: '/' },
  { label: 'Reading', to: '/reading' },
  { label: 'Chat', to: '/chat' },
  { label: 'Radar', to: '/radar' },
];

function isActivePath(currentPath: string, targetPath: string) {
  if (targetPath === '/') {
    return currentPath === '/';
  }
  return currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
}

export default function Navbar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [quickSearch, setQuickSearch] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const initials = useMemo(() => user?.username?.charAt(0).toUpperCase() || 'D', [user?.username]);

  const handleQuickSearch = (event: React.FormEvent) => {
    event.preventDefault();
    if (!quickSearch.trim()) {
      return;
    }
    navigate(`/word/${encodeURIComponent(quickSearch.trim())}?lang=en`);
    setQuickSearch('');
  };

  const navContent = NAV_ITEMS.map((item) => {
    const active = isActivePath(pathname, item.to);
    return (
      <Button
        key={item.to}
        component={NavLink}
        to={item.to}
        color="inherit"
        sx={{
          borderRadius: 0,
          borderBottom: active ? '2px solid #1a73e8' : '2px solid transparent',
          color: active ? '#1a73e8' : '#374151',
          fontWeight: active ? 700 : 600,
          px: 1.5,
          py: 2.5,
        }}
      >
        {item.label}
      </Button>
    );
  });

  return (
    <AppBar position="sticky" color="transparent" elevation={0} sx={{ bgcolor: '#fff', borderBottom: '1px solid #e5e7eb' }}>
      <Toolbar sx={{ minHeight: '64px !important', gap: 2, px: { xs: 2, md: 4 } }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
          <IconButton sx={{ display: { xs: 'inline-flex', md: 'none' } }} onClick={() => setMobileOpen(true)}>
            <MenuIcon />
          </IconButton>
          <Typography
            component={NavLink}
            to="/"
            sx={{
              color: '#111827',
              fontWeight: 800,
              fontSize: 24,
              textDecoration: 'none',
              letterSpacing: '-0.03em',
            }}
          >
            DictZoom
          </Typography>
        </Stack>

        <Box sx={{ flex: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {navContent}
          </Stack>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 'auto' }}>
          {pathname === '/radar' && (
            <Box component="form" onSubmit={handleQuickSearch} sx={{ display: { xs: 'none', lg: 'block' } }}>
              <TextField
                value={quickSearch}
                onChange={(event) => setQuickSearch(event.target.value)}
                placeholder="Quick search"
                size="small"
                sx={{ minWidth: 220, '& .MuiOutlinedInput-root': { bgcolor: '#f8fafc' } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          )}
          <IconButton aria-label="notifications">
            <Badge color="primary" variant="dot">
              <NotificationsNoneRoundedIcon />
            </Badge>
          </IconButton>
          <IconButton aria-label="settings">
            <SettingsOutlinedIcon />
          </IconButton>
          <IconButton onClick={(event) => setMenuAnchor(event.currentTarget)}>
            <Avatar sx={{ bgcolor: '#111827', width: 36, height: 36 }}>{initials}</Avatar>
          </IconButton>
        </Stack>
      </Toolbar>

      <Drawer anchor="left" open={mobileOpen} onClose={() => setMobileOpen(false)}>
        <Box sx={{ width: 260, p: 2 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 22, mb: 2 }}>DictZoom</Typography>
          <Stack spacing={1}>
            {NAV_ITEMS.map((item) => (
              <Button
                key={item.to}
                component={NavLink}
                to={item.to}
                onClick={() => setMobileOpen(false)}
                sx={{ justifyContent: 'flex-start', color: isActivePath(pathname, item.to) ? '#1a73e8' : '#111827' }}
              >
                {item.label}
              </Button>
            ))}
          </Stack>
        </Box>
      </Drawer>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem disabled>{user?.email || user?.username || 'Signed in'}</MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            logout();
            navigate('/login');
          }}
        >
          Logout
        </MenuItem>
      </Menu>
    </AppBar>
  );
}
